import { useState, useEffect } from 'react';
import { FiX, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { HiCog } from 'react-icons/hi';
import { useAuth } from '~/contexts/AuthContext';
import { useUpdatePhone } from '~/hooks/usePhoneNumber';
import PhoneNumberInput from '~/components/app/components/PhoneNumberInput';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UserSettingsModal({
  isOpen,
  onClose,
}: UserSettingsModalProps) {
  const { user, refreshAuth } = useAuth();
  const updatePhoneMutation = useUpdatePhone();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Initialize phone number from user
  useEffect(() => {
    if (user?.phone_number) {
      setPhoneNumber(user.phone_number);
      // Extract digits (remove country code which is 1-3 digits)
      const digits = user.phone_number.replace(/^\+\d{1,3}/, '');
      setPhoneDigits(digits);
    }
  }, [user]);

  const validatePhone = (phone: string): boolean => {
    // Must have country code + phone number (minimum 8 digits total)
    const pattern = /^\+[1-9]\d{7,14}$/;
    return pattern.test(phone);
  };

  const handleSave = async () => {
    if (!phoneDigits || phoneDigits.length === 0) {
      setPhoneError('Please enter your phone number');
      return;
    }

    if (phoneDigits.length < 6) {
      setPhoneError('Phone number is too short');
      return;
    }

    if (!validatePhone(phoneNumber)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }

    try {
      await updatePhoneMutation.mutateAsync({ phone_number: phoneNumber });
      setPhoneError('');
      // Refresh auth context to update user data
      await refreshAuth();
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (error: any) {
      setPhoneError(
        error.response?.data?.detail || 'Failed to update phone number'
      );
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal modal-open">
        <div className="modal-box max-w-md">
          <button
            onClick={onClose}
            disabled={updatePhoneMutation.isPending}
            className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          >
            <FiX className="w-4 h-4" />
          </button>

          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <HiCog className="w-6 h-6 text-primary" />
            </div>
          </div>

          <h3 className="font-semibold text-lg text-center mb-2">Settings</h3>
          <p className="text-center text-sm text-base-content/70 mb-6">
            Update your notification preferences
          </p>

          {/* Phone Number Section */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-2">
                Phone Number (WhatsApp)
              </label>
              <PhoneNumberInput
                value={phoneNumber}
                onChange={(phone) => {
                  setPhoneNumber(phone);
                  // Extract just the digits (remove country code which is 1-3 digits)
                  const digits = phone.replace(/^\+\d{1,3}/, '');
                  setPhoneDigits(digits);
                  setPhoneError('');
                }}
                error={phoneError}
                placeholder="Enter phone number"
                disabled={updatePhoneMutation.isPending}
              />
            </div>
            <p className="text-xs text-base-content/60">
              You'll receive report notifications on WhatsApp
            </p>
          </div>

          {/* Action Buttons */}
          <div className="modal-action justify-center gap-3">
            <button
              onClick={onClose}
              disabled={updatePhoneMutation.isPending}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updatePhoneMutation.isPending || phoneDigits.length === 0}
              className="btn btn-primary"
            >
              {updatePhoneMutation.isPending ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
        <div className="modal-backdrop bg-black/40" onClick={onClose} />
      </div>

      {/* Success/Error Toasts */}
      {(showSuccess || showError) && (
        <div className="toast toast-top toast-center z-50">
          <div
            className={`alert ${
              showSuccess ? 'alert-success' : 'alert-error'
            } transition-all duration-300 ease-in-out`}
          >
            {showSuccess ? (
              <FiCheckCircle className="h-6 w-6" />
            ) : (
              <FiXCircle className="h-6 w-6" />
            )}
            <span>
              {showSuccess
                ? 'Phone number updated successfully'
                : 'Failed to update phone number'}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
